import * as React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';

import Demo, { DemoSliderControl } from '@/components/Demo';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';

import Ball from './DynamicBall';
import CheckmarkButton from './CheckmarkButton';
import { constructSnippet } from './ToDynamicOpacityDemo.helpers';

interface Props {
  initialIsToggled?: boolean;
  initialTwinkleSpeed?: number;
  codeOutput: React.ReactNode;
}

function ToDynamicOpacityDemo({
  codeOutput,
  initialIsToggled = false,
  initialTwinkleSpeed = 250,
}: Props) {
  const [isToggled, setIsToggled] = React.useState(initialIsToggled);

  return (
    <OuterWrapper>
      <Demo
        initialValues={{
          twinkleSpeed: initialTwinkleSpeed,
        }}
        outputWrapper={null}
        controlPosition="top"
        customGrid={{
          gridTemplateColumns: 'min(400px, 100%)',
          justifyContent: 'center',
        }}
        controls={[
          <DemoSliderControl
            key="twinkleSpeed"
            id="twinkleSpeed"
            label="Twinkle Duration"
            min={100}
            max={600}
            step={50}
          />,
        ]}
      >
        {({ twinkleSpeed }) => {
          const codeSnippet = constructSnippet(
            twinkleSpeed,
            isToggled
          );

          return (
            <Wrapper>
              <Demonstration data-can-be-annotated="false">
                <Ball
                  twinkleSpeed={twinkleSpeed}
                  isToggled={isToggled}
                />
                <Button
                  isToggled={isToggled}
                  onClick={() => setIsToggled(!isToggled)}
                >
                  Render element
                </Button>
              </Demonstration>
              <SnippetWrapper>
                <CodeSnippetProvider lang="html" code={codeSnippet}>
                  {codeOutput}
                </CodeSnippetProvider>
              </SnippetWrapper>
            </Wrapper>
          );
        }}
      </Demo>
    </OuterWrapper>
  );
}

// On larger windows, we want the text in the code snippet to line up with the body paragraphs:
const OuterWrapper = styled.div`
  @media (min-width: 50rem) {
    margin-inline: -16px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  flex-wrap: wrap;
  gap: 16px;

  @media ${BREAKPOINTS.smAndSmaller} {
    flex-direction: column;
  }

  @media ${BREAKPOINTS.smAndLarger} {
    margin: -16px;
    margin-top: 0;
  }
`;

const SnippetWrapper = styled.div`
  --code-font-size: 0.875rem;
  --code-line-height: 1.325rem;
  flex: 2;
  width: 25.25rem;
  min-width: 25.25rem;
  min-height: 36.5rem;
  border: 1px solid var(--color-gray-300);
  border-radius: 8px;
  text-align: left;
  white-space: pre-wrap;
  font-size: 0.875rem;

  @media ${BREAKPOINTS.smAndSmaller} {
    width: 100%;
    min-width: 0;
    min-height: 0;
    max-width: 100%;
    overflow-x: auto;
  }
`;

const Demonstration = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 32px;
  min-width: 18rem;
  padding: 16px;
  border: 1px solid var(--color-gray-300);
  border-radius: 8px;

  @media ${BREAKPOINTS.smAndSmaller} {
    gap: 16px;
    padding-block: 24px;
  }
`;

const Button = styled(CheckmarkButton)`
  width: 11rem;
  height: 3rem;
  padding: 0;
`;

export default ToDynamicOpacityDemo;
